/**
 * California statewide GHG by economic sector (Figure 4), MMTCO₂e → metric tons for charts.
 * Source: CARB "2000–2023 GHG Emissions Trends Report Data", Figure 4 sheet.
 * https://ww2.arb.ca.gov/ghg-inventory-data
 */

export const CARB_FIGURE4_YEAR_RANGE = [2000, 2001, 2002, 2003, 2004, 2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014, 2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023] as const;

export const CARB_MT_TRANSPORTATION: Record<number, number> = { 2000: 176505553, 2001: 175645417, 2002: 182862000, 2003: 181459177, 2004: 181841870, 2005: 182559737, 2006: 182546310, 2007: 182050512, 2008: 170878555, 2009: 164669781, 2010: 161925445, 2011: 158475452, 2012: 156537668, 2013: 156355618, 2014: 157214418, 2015: 160903321, 2016: 164599799, 2017: 165850638, 2018: 164558624, 2019: 161796160, 2020: 135522765, 2021: 145330419, 2022: 140130716, 2023: 133675604 };
export const CARB_MT_ELECTRIC_POWER: Record<number, number> = { 2000: 104749464, 2001: 121906904, 2002: 108551356, 2003: 112521066, 2004: 115105345, 2005: 107761667, 2006: 104433783, 2007: 113837165, 2008: 120054383, 2009: 101291987, 2010: 90299792, 2011: 89201543, 2012: 99435612, 2013: 94009948, 2014: 90277618, 2015: 86233715, 2016: 70769875, 2017: 64357800, 2018: 65008902, 2019: 60179510, 2020: 59460060, 2021: 62327099, 2022: 59740512, 2023: 57234452 };
export const CARB_MT_INDUSTRIAL: Record<number, number> = { 2000: 86638784, 2001: 86309497, 2002: 87420295, 2003: 85830688, 2004: 89223455, 2005: 86955190, 2006: 86841264, 2007: 84040871, 2008: 82167999, 2009: 80029609, 2010: 82388881, 2011: 80425694, 2012: 78041382, 2013: 79503662, 2014: 83100642, 2015: 80541695, 2016: 79452433, 2017: 78859693, 2018: 79480866, 2019: 77740759, 2020: 71139906, 2021: 71183231, 2022: 70329638, 2023: 66942404 };
export const CARB_MT_COMMERCIAL_AND_RESIDENTIAL: Record<number, number> = { 2000: 44796377, 2001: 43105965, 2002: 43766257, 2003: 42676531, 2004: 45429180, 2005: 43455879, 2006: 43533587, 2007: 44039273, 2008: 44543894, 2009: 44815695, 2010: 46267750, 2011: 46102850, 2012: 42724158, 2013: 43231428, 2014: 37694818, 2015: 39430561, 2016: 40171836, 2017: 40649224, 2018: 40208593, 2019: 43452686, 2020: 42001103, 2021: 41806086, 2022: 41827224, 2023: 43327631 };
export const CARB_MT_AGRICULTURE: Record<number, number> = { 2000: 32332414, 2001: 32937849, 2002: 33565675, 2003: 33457751, 2004: 33401040, 2005: 33762193, 2006: 34578538, 2007: 34942617, 2008: 35278913, 2009: 33389918, 2010: 33708367, 2011: 33811912, 2012: 34927585, 2013: 33869750, 2014: 33956629, 2015: 32924352, 2016: 32624332, 2017: 31926899, 2018: 32392562, 2019: 31714311, 2020: 31521017, 2021: 30665984, 2022: 30120028, 2023: 29243395 };
export const CARB_MT_HIGH_GWP: Record<number, number> = { 2000: 6637524, 2001: 6920528, 2002: 7379173, 2003: 8037655, 2004: 8745500, 2005: 9504791, 2006: 10383219, 2007: 11132903, 2008: 11942388, 2009: 12517016, 2010: 13750209, 2011: 14796073, 2012: 15791352, 2013: 16849531, 2014: 17925838, 2015: 18802068, 2016: 19532515, 2017: 20097852, 2018: 20641801, 2019: 20793486, 2020: 21343013, 2021: 21270877, 2022: 21320848, 2023: 21434576 };
export const CARB_MT_RECYCLING_AND_WASTE: Record<number, number> = { 2000: 7053265, 2001: 7222730, 2002: 7173971, 2003: 7311055, 2004: 7293882, 2005: 7494381, 2006: 7567723, 2007: 7614472, 2008: 7745996, 2009: 7831404, 2010: 7904645, 2011: 8233881, 2012: 8377724, 2013: 8303830, 2014: 8157308, 2015: 8096682, 2016: 7870070, 2017: 8144354, 2018: 8153099, 2019: 8230511, 2020: 8424573, 2021: 8262064, 2022: 8114288, 2023: 8552128 };

export type CarbFigure4SectorKey =
  | 'Transportation'
  | 'Electric Power'
  | 'Industrial'
  | 'Commercial & Residential'
  | 'Agriculture'
  | 'High GWP'
  | 'Recycling & Waste';

/** Stack order bottom → top (matches CARB Figure 4). */
export const CARB_FIGURE4_STACK_ORDER: CarbFigure4SectorKey[] = [
  'Transportation',
  'Electric Power',
  'Industrial',
  'Commercial & Residential',
  'Agriculture',
  'High GWP',
  'Recycling & Waste',
];

/**
 * Stack order for side-by-side comparison charts only: Industrial & High GWP on top;
 * Electric Power immediately below Industrial (aligned with ClimatePlans left panel).
 */
export const CARB_FIGURE4_HARMONIZED_DISPLAY_ORDER: CarbFigure4SectorKey[] = [
  'Transportation',
  'Commercial & Residential',
  'Agriculture',
  'Recycling & Waste',
  'Electric Power',
  'Industrial',
  'High GWP',
];

const KEY_MAP: Record<CarbFigure4SectorKey, Record<number, number>> = {
  Transportation: CARB_MT_TRANSPORTATION,
  'Electric Power': CARB_MT_ELECTRIC_POWER,
  Industrial: CARB_MT_INDUSTRIAL,
  'Commercial & Residential': CARB_MT_COMMERCIAL_AND_RESIDENTIAL,
  Agriculture: CARB_MT_AGRICULTURE,
  'High GWP': CARB_MT_HIGH_GWP,
  'Recycling & Waste': CARB_MT_RECYCLING_AND_WASTE,
};

export function carbFigure4SectorMt(sector: CarbFigure4SectorKey, year: number): number | null {
  const t = KEY_MAP[sector];
  const v = t[year];
  return v != null && Number.isFinite(v) ? v : null;
}
